import React from 'react';
import { Search, Check, X, Loader2, FileText, Gift } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { formatPrice } from '../../lib/utils';

export const CreateOfferDialog = ({
    open,
    onOpenChange,
    formData,
    setFormData,
    products,
    clients,
    searchProduct,
    setSearchProduct,
    searchClient,
    setSearchClient,
    quoteNumber,
    setQuoteNumber,
    isLoadingQuote,
    importedQuoteNumber,
    isCreating,
    onSubmit,
    onImportQuote,
    onCancel,
}) => {
    const filteredProducts = products.filter(p =>
        p.name.toLowerCase().includes(searchProduct.toLowerCase()) ||
        p.product_code?.toLowerCase().includes(searchProduct.toLowerCase())
    );

    const filteredClients = clients.filter(c =>
        c.company_name?.toLowerCase().includes(searchClient.toLowerCase()) ||
        c.email?.toLowerCase().includes(searchClient.toLowerCase()) ||
        c.client_code?.toLowerCase().includes(searchClient.toLowerCase())
    );

    const toggleProductSelection = (product) => {
        const existing = formData.products.find(p => p.product_id === product.id);
        if (existing) {
            setFormData({
                ...formData,
                products: formData.products.filter(p => p.product_id !== product.id)
            });
        } else {
            setFormData({
                ...formData,
                products: [...formData.products, {
                    product_id: product.id,
                    product_name: product.name,
                    price: product.price,
                    unit: product.unit,
                    image_url: product.image_url
                }]
            });
        }
    };

    const toggleClientSelection = (clientId) => {
        if (formData.target_client_ids.includes(clientId)) {
            setFormData({
                ...formData,
                target_client_ids: formData.target_client_ids.filter(id => id !== clientId)
            });
        } else {
            setFormData({
                ...formData,
                target_client_ids: [...formData.target_client_ids, clientId]
            });
        }
    };

    const selectAllClients = () => {
        const filteredClientIds = filteredClients.map(c => c.id);
        setFormData({
            ...formData,
            target_client_ids: [...new Set([...formData.target_client_ids, ...filteredClientIds])]
        });
    };

    const deselectAllClients = () => {
        setFormData({ ...formData, target_client_ids: [] });
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
                <DialogHeader>
                    <DialogTitle>Creer une offre personnalisee</DialogTitle>
                    <DialogDescription>
                        Selectionnez les produits, definissez les prix et choisissez les clients concernes
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={onSubmit}>
                    <div className="space-y-6 py-4">
                        {/* Import from Quote */}
                        <div className="p-4 rounded-xl bg-amber-50 border border-amber-200">
                            <Label className="text-amber-800 font-semibold mb-2 block">Importer depuis une demande de prix (optionnel)</Label>
                            <div className="flex gap-2">
                                <Input
                                    placeholder="N° demande (ex: DV-20260320-0001)"
                                    value={quoteNumber}
                                    onChange={(e) => setQuoteNumber(e.target.value)}
                                    className="flex-1 bg-white"
                                    data-testid="quote-number-input"
                                />
                                <Button type="button" onClick={onImportQuote} disabled={isLoadingQuote || !quoteNumber.trim()} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="import-quote-btn">
                                    {isLoadingQuote ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4 mr-1" />}
                                    Importer
                                </Button>
                            </div>
                            {importedQuoteNumber && (
                                <p className="text-xs text-amber-700 mt-2">Demande {importedQuoteNumber} importee. Definissez les prix ci-dessous.</p>
                            )}
                        </div>

                        {/* Title & Description */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="title">Titre de l'offre *</Label>
                                <Input
                                    id="title"
                                    value={formData.title}
                                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                    placeholder="Ex: Offre speciale Franprix"
                                    data-testid="offer-title-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="expires">Date d'expiration (optionnel)</Label>
                                <Input
                                    id="expires"
                                    type="date"
                                    value={formData.expires_at}
                                    onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="description">Description (optionnel)</Label>
                            <Textarea
                                id="description"
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                placeholder="Description de l'offre..."
                                rows={2}
                            />
                        </div>

                        {/* Products Selection */}
                        <div className="space-y-3">
                            <Label>Produits de l'offre ({formData.products.length} selectionne(s))</Label>

                            {formData.products.length > 0 && (
                                <div className="border border-green-200 rounded-lg bg-green-50/50 p-3 space-y-2" data-testid="selected-products-summary">
                                    <p className="text-xs font-semibold text-green-800 mb-2">Produits selectionnes - Definissez les prix :</p>
                                    {formData.products.map((sp, idx) => (
                                        <div key={sp.product_id || idx} className="flex items-center gap-2 bg-white rounded-lg p-2 border border-green-200">
                                            <span className="flex-1 text-sm font-medium text-jungle truncate">{sp.product_name}</span>
                                            <Input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={sp.price || ''}
                                                onChange={(e) => {
                                                    const newProducts = [...formData.products];
                                                    newProducts[idx] = { ...newProducts[idx], price: parseFloat(e.target.value) || 0 };
                                                    setFormData({ ...formData, products: newProducts });
                                                }}
                                                className="w-24 h-8"
                                                placeholder="Prix"
                                                data-testid={`price-input-${idx}`}
                                            />
                                            <span className="text-xs text-muted-foreground whitespace-nowrap">EUR/{sp.unit}</span>
                                            <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:bg-red-50" onClick={() => {
                                                setFormData({ ...formData, products: formData.products.filter((_, i) => i !== idx) });
                                            }}>
                                                <X className="h-3 w-3" />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input
                                    placeholder="Rechercher un produit..."
                                    value={searchProduct}
                                    onChange={(e) => setSearchProduct(e.target.value)}
                                    className="pl-9"
                                />
                            </div>
                            <div className="border rounded-lg max-h-48 overflow-y-auto">
                                {filteredProducts.map((product) => {
                                    const isSelected = formData.products.some(p => p.product_id === product.id);
                                    return (
                                        <div
                                            key={product.id}
                                            className={`flex items-center gap-3 p-3 border-b last:border-b-0 cursor-pointer transition-colors ${isSelected ? 'bg-green-100 border-green-200' : 'hover:bg-gray-50'}`}
                                            onClick={() => toggleProductSelection(product)}
                                        >
                                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${isSelected ? 'bg-green-600 border-green-600' : 'border-gray-300'}`}>
                                                {isSelected && <Check className="h-3 w-3 text-white" />}
                                            </div>
                                            <img src={product.image_url} alt={product.name} className="w-10 h-10 rounded object-cover" />
                                            <div className="flex-1">
                                                <p className="font-medium text-sm">{product.name}</p>
                                            </div>
                                            <span className="text-sm text-muted-foreground">{formatPrice(product.price)}/{product.unit}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Clients Selection */}
                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <Label>Clients concernes ({formData.target_client_ids.length} selectionne(s))</Label>
                                <div className="flex gap-2">
                                    <Button type="button" variant="outline" size="sm" onClick={selectAllClients}>
                                        Tout selectionner
                                    </Button>
                                    <Button type="button" variant="outline" size="sm" onClick={deselectAllClients}>
                                        Tout deselectionner
                                    </Button>
                                </div>
                            </div>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input
                                    placeholder="Rechercher un client..."
                                    value={searchClient}
                                    onChange={(e) => setSearchClient(e.target.value)}
                                    className="pl-9"
                                />
                            </div>
                            <div className="border rounded-lg max-h-48 overflow-y-auto">
                                {filteredClients.map((client) => {
                                    const isSelected = formData.target_client_ids.includes(client.id);
                                    return (
                                        <div
                                            key={client.id}
                                            className={`flex items-center gap-3 p-3 border-b last:border-b-0 cursor-pointer transition-colors ${isSelected ? 'bg-purple-100 border-purple-200' : 'hover:bg-gray-50'}`}
                                            onClick={() => toggleClientSelection(client.id)}
                                        >
                                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${isSelected ? 'bg-purple-600 border-purple-600' : 'border-gray-300'}`}>
                                                {isSelected && <Check className="h-3 w-3 text-white" />}
                                            </div>
                                            <div className="flex-1">
                                                <p className="font-medium text-sm">{client.company_name}</p>
                                                <p className="text-xs text-muted-foreground">{client.email}</p>
                                            </div>
                                            <Badge className="bg-blue-100 text-blue-800 text-xs">
                                                {client.client_category || 'Autre'}
                                            </Badge>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={onCancel} data-testid="cancel-offer-btn">
                            Annuler
                        </Button>
                        <Button type="submit" className="bg-jungle hover:bg-jungle/90" disabled={isCreating}>
                            <Gift className="h-4 w-4 mr-2" />
                            {isCreating ? 'Creation en cours...' : "Creer l'offre et notifier"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};
